import React, { useState } from "react";
import { motion } from "@/lib/framer-motion-shim";
import { Camera, Instagram, Mail, Phone, MapPin, Calendar, ChevronRight } from "lucide-react";
import { useLocation } from "react-router-dom";
import BookingModal from "@/components/demos/BookingModal";
import ContactForm from "@/components/demos/ContactForm";
import DemoTopBar from "@/components/demos/DemoTopBar";
import Seo from "@/components/Seo";

const images = [
  "https://images.unsplash.com/photo-1768248468040-259dc015e0d9?w=800&q=80",
  "https://images.unsplash.com/photo-1712000310664-309d13ed48ae?w=800&q=80",
  "https://images.unsplash.com/photo-1613571715029-7d81019f24e7?w=800&q=80",
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800&q=80",
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80",
  "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=800&q=80",
];

const services = [
  { name: "Sessão de Retrato", price: "150€", description: "Sessão individual ou casal, 1 hora" },
  { name: "Cobertura de Casamento", price: "1200€", description: "Dia completo com 300+ fotos editadas" },
  { name: "Fotografia Corporativa", price: "400€", description: "Headshots e ambiente de trabalho" },
  { name: "Sessão de Família", price: "200€", description: "Até 6 pessoas, 1.5 horas" },
];

export default function PhotographerDemo() {
  const location = useLocation();
  const [bookingOpen, setBookingOpen] = useState(false);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("agendar") === "1") {
      setBookingOpen(true);
    }
  }, [location.search]);

  return (
    <>
      <Seo
        title="Demo Fotógrafo | Web & Studio"
        description="Template demo para fotógrafo com portfólio visual, serviços e contacto."
        path="/demo/photographer"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="min-h-screen bg-zinc-950 text-zinc-50 theme-photographer"
      >
        <DemoTopBar />

      {/* Hero */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images[0]}
            alt="Hero"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/60 via-transparent to-zinc-950" />
        </div>
        
        <div className="relative z-10 text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Camera className="w-8 h-8" />
            </div>
            <h1 className="text-6xl md:text-8xl font-bold tracking-tight mb-4">Studio Noir</h1>
            <p className="text-xl md:text-2xl text-zinc-400 font-light tracking-wide">Fotografia Artística</p>
          </motion.div>
          
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            onClick={() => setBookingOpen(true)}
            className="mt-12 px-8 py-4 bg-white text-black font-medium tracking-wider rounded-none hover:bg-zinc-200 transition-colors duration-200"
            data-testid="hero-booking-btn"
          >
            AGENDAR SESSÃO
          </motion.button>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="w-px h-20 bg-gradient-to-b from-transparent via-zinc-500 to-transparent" />
        </div>
      </section>

      {/* About */}
      <section className="py-24 lg:py-32">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-sm text-zinc-500 uppercase tracking-[0.3em]">Sobre Nós</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6">A Arte de Capturar Momentos</h2>
              <p className="text-zinc-400 text-lg leading-relaxed mb-6">
                Com mais de 10 anos de experiência, o Studio Noir dedica-se a criar imagens 
                que contam histórias. Cada fotografia é uma obra de arte única, capturada 
                com atenção meticulosa aos detalhes e iluminação.
              </p>
              <p className="text-zinc-400 text-lg leading-relaxed">
                Especializados em retratos, casamentos e fotografia corporativa, 
                trabalhamos para transformar momentos em memórias eternas.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={images[1]}
                alt="About"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 border border-zinc-700" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 lg:py-32 bg-zinc-900/50">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm text-zinc-500 uppercase tracking-[0.3em]">Portfólio</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4">Trabalhos Recentes</h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
            {images.map((img, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative group aspect-square overflow-hidden cursor-pointer"
              >
                <img
                  src={img}
                  alt={`Portfolio ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <span className="text-white font-medium">Ver</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 lg:py-32">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm text-zinc-500 uppercase tracking-[0.3em]">Serviços</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4">O Que Oferecemos</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group p-8 border border-zinc-800 hover:border-zinc-600 transition-colors duration-300 cursor-pointer"
                onClick={() => setBookingOpen(true)}
                data-testid={`service-card-${index}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-2xl font-semibold">{service.name}</h3>
                  <span className="text-2xl font-light text-zinc-400">{service.price}</span>
                </div>
                <p className="text-zinc-500 mb-4">{service.description}</p>
                <div className="flex items-center gap-2 text-zinc-400 group-hover:text-white transition-colors duration-300">
                  <span className="text-sm">Reservar</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-24 lg:py-32 bg-zinc-900/50">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-sm text-zinc-500 uppercase tracking-[0.3em]">Contacto</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-8">Vamos Trabalhar Juntos</h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 border border-zinc-700 flex items-center justify-center">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Telefone</p>
                    <p className="text-lg">+351 912 345 678</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 border border-zinc-700 flex items-center justify-center">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Email</p>
                    <p className="text-lg">contacto@webestudio.pt</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 border border-zinc-700 flex items-center justify-center">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Estúdio</p>
                    <p className="text-lg">Rua da Fotografia, 123 - Lisboa</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 border border-zinc-700 flex items-center justify-center hover:border-white transition-colors duration-200">
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <ContactForm demoType="photographer" variant="minimal" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-800">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-zinc-500">© 2024 Studio Noir</p>
            <p className="text-zinc-600 text-sm">Demo Template - Fotógrafo</p>
          </div>
        </div>
      </footer>

        <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} demoType="photographer" />
      </motion.div>
    </>
  );
}




